/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [friend-entry.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 07.03.20, 14:44
 */
import React, {Fragment, useEffect, useState} from 'react';

import {WavesTooltipBounded} from "../../components/navigation/buttons/nav-buttons";
import {UserBoxedAnchor, UserBoxedLink} from "../../components/navigation/links/nav-headlines";
import {STATE_ACTIVE, STATE_BLOCKED, subtractChatCount} from "../../actions/spaces";
import DeleteFriendDialog from "./dialogs/delete-friend-dialog";
import {NavigationGroup, NavigationRow} from "../../components/navigation/layout/nav-layout";
import {useLanguage} from "../../components/configuration/configuration";
import {useAuthorization, useFriendData} from "../../selectors";
import {useDispatch} from "react-redux";
import {PersonAdd} from "@material-ui/icons";
import {ChatIcon, PersonOffIcon} from "../../components/navigation/icons/nav-icons";


const FriendEntry = ({chatEntry, enableChat, onAddFriend, onBlock, onUnblock, onDelete, onChat}) => {
    const [unreadCount, setUnreadCount] = useState(chatEntry.chat.delivered);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const {principalUsername} = useAuthorization();
    const {friend: activeFriend, incomingChat} = useFriendData();
    const dispatch = useDispatch();
    const Lang = useLanguage();

    const {chat, friend, state, action, friendFromPrincipal} = chatEntry;

    // a chat is open
    useEffect(() => {
        if (activeFriend) {
            setIsChatOpen(activeFriend.chat.id === chat.id);
        }
    }, [activeFriend])

    // handle new incoming messages thru incomingChat which is a one time shot for signaling a new incoming message
    useEffect(() => {
        if (incomingChat) {
            // is the new message not consumed yet and targeted for the currently open chat?
            const isDeliverable = (incomingChat.state === 'DELIVERED') && (incomingChat.chat.id === chat.id);
            if (isDeliverable && !isChatOpen) {
                setUnreadCount(unreadCount + 1);

            } else if (isDeliverable && isChatOpen) {
                dispatch(subtractChatCount(1));
            }
        }
    }, [incomingChat]);

    const handleOnOpenChat = event => {
        dispatch(subtractChatCount(unreadCount));
        setUnreadCount(0);
        onChat(event);
    }

    const getUserBoxBigLink = (isBlocked = false) => {
        const homespace = `/${friend.username}/home`;

        if(enableChat) {
            return <UserBoxedAnchor badged={enableChat && !isBlocked && (unreadCount > 0) ? unreadCount : null}
                                   active={enableChat && isChatOpen}
                                   blocked={isBlocked}
                                   onClick={handleOnOpenChat}
                                   user={friend}/>
        }
        return <UserBoxedLink grayscale
                            badged={enableChat && !isBlocked && (unreadCount > 0) ? unreadCount : null}
                            active={enableChat && isChatOpen}
                            blocked={isBlocked}
                            to={homespace}
                            user={friend}/>
    }

    const isBlocked = state === STATE_BLOCKED;
    const isSelf = principalUsername === friend.username;


    return <NavigationRow key={friend.id} className='friends-generic-entry'>
        <NavigationGroup>
            {getUserBoxBigLink(isBlocked)}
        </NavigationGroup>

        {enableChat && <NavigationGroup>
            {isBlocked && action === 'BLOCKING' &&
                <WavesTooltipBounded circle title={Lang.chat.active.unblock(friend.firstname)}
                                     className='generic-navigation-button' onClick={onUnblock}>
                    <PersonOffIcon aria-hidden="true"/>
                </WavesTooltipBounded>}

            {state === STATE_ACTIVE && <Fragment>
                <WavesTooltipBounded circle title={Lang.chat.active.block(friend.firstname)}
                                     className='generic-navigation-button' onClick={onBlock}>
                    <PersonOffIcon aria-hidden="true"/>
                </WavesTooltipBounded>

                <DeleteFriendDialog friend={friend} onDelete={onDelete}/>
                <WavesTooltipBounded className='generic-navigation-button' circle
                                     title={Lang.chat.active.chat(friend.firstname)}
                                     onClick={handleOnOpenChat}>
                    <ChatIcon aria-hidden="true"/>
                </WavesTooltipBounded>
            </Fragment>}
        </NavigationGroup>}

        {!enableChat && !isSelf && !friendFromPrincipal && <NavigationGroup>
            <WavesTooltipBounded circle title={Lang.chat.inactive.addFriend(friend.firstname)}
                                 className='generic-navigation-button' onClick={onAddFriend}>
                <PersonAdd aria-hidden="true"/>
            </WavesTooltipBounded>
        </NavigationGroup>}

    </NavigationRow>
};

export default FriendEntry;